/**
 * Storage helpers — thin wrappers over Firebase Cloud Storage.
 *
 * Today this only handles profile avatars, but the upload helper is
 * generic enough to grow into the "filestore" surface later.
 */

import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage, useFirebaseAuth } from './firebase';

function store() {
  if (!storage) {
    throw new Error(
      '[storage] Firebase Storage is not initialized. Check VITE_FIREBASE_* env vars.',
    );
  }
  return storage;
}

/** Map a MIME type to a sane file extension for the stored object. */
function extFor(file: File): string {
  const fromType = file.type.split('/')[1];
  if (fromType) return fromType.replace('jpeg', 'jpg');
  const fromName = file.name.split('.').pop();
  return fromName ? fromName.toLowerCase() : 'bin';
}

/**
 * Upload a profile avatar for `uid` and return its public download URL.
 *
 * Path: `avatars/{uid}/avatar.{ext}`. A stable path means re-uploading
 * overwrites the previous photo instead of leaving orphans, and storage
 * rules can gate writes on the `{uid}` segment.
 */
export async function uploadAvatar(uid: string, file: File): Promise<string> {
  const objectRef = ref(store(), `avatars/${uid}/avatar.${extFor(file)}`);
  await uploadBytes(objectRef, file, { contentType: file.type });
  return getDownloadURL(objectRef);
}

/** A stored file attachment: durable URL plus the metadata the record keeps. */
export interface UploadedFile {
  url: string;
  mime: string;
  size: number;
}

/** Inline-fallback cap in dev (no Storage): keep localStorage sane. */
const DEV_INLINE_MAX = 2 * 1024 * 1024;

/**
 * Upload a project/task file attachment and return a durable URL + metadata.
 *
 * Live (Firebase Storage configured): uploads to
 * `attachments/{scope}/{id}/{ts}-{safeName}` and returns the download URL —
 * the production path (gated by `storage.rules`). Dev/mock (no Storage):
 * falls back to an inline data URL so the *real* file is stored and the
 * feature works end-to-end — never a fake link. Guarded by size so the
 * localStorage store can't blow up; larger files require live Storage.
 */
export async function uploadAttachment(
  scope: 'project' | 'task',
  id: string,
  file: File,
): Promise<UploadedFile> {
  // Only hit the real bucket when Firebase Auth is live — Storage rules
  // require a verified @argoiq.com token, so in mock/dev auth we must NOT
  // touch the bucket (it 403s); fall back to an inline data URL instead.
  if (storage && useFirebaseAuth) {
    const safe = file.name.replace(/[^\w.-]+/g, '_');
    const objectRef = ref(storage, `attachments/${scope}/${id}/${Date.now()}-${safe}`);
    await uploadBytes(objectRef, file, { contentType: file.type });
    const url = await getDownloadURL(objectRef);
    return { url, mime: file.type, size: file.size };
  }
  if (file.size > DEV_INLINE_MAX) {
    throw new Error(
      `File is ${(file.size / 1024 / 1024).toFixed(1)}MB — too large for local mode ` +
        '(2MB cap). Configure Firebase Storage (VITE_FIREBASE_STORAGE_BUCKET) to upload larger files.',
    );
  }
  const dataUrl = await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(reader.error ?? new Error('Failed to read file.'));
    reader.readAsDataURL(file);
  });
  return { url: dataUrl, mime: file.type, size: file.size };
}
